import { FC, ReactNode } from 'react';
import ELEMENT from 'common/element';
import { HELP_ROUTE } from 'components/help/constants';
import Link from 'components/common/Link/Link';
import ScalyrPageHeader, { ButtonConfig } from 'components/common/PageHeader/PageHeader';
import { ButtonVariant } from 'components/common/Button/Button';

type PageHeaderProps = {
  name: string;
  inputTouched: boolean;
  isWriteDisabled: boolean;
  onSaveFile: () => void;
  onDeleteFile: () => void;
  onPrettyPrint: () => void;
};

type ConfigFileHeaderProps = {
  name?: string;
  type?: string;
  viewLink?: string;
  extra?: ReactNode;
  helpLink?: string;
  customName?: string;
};

const additionalRightButtons = (
  inputTouched: boolean,
  onSaveFile: () => void,
  onPrettyPrint: () => void,
): Array<ButtonConfig> => [
  {
    name: 'Prettyprint',
    onClick: onPrettyPrint,
    variant: ButtonVariant.SECONDARY,
    disabled: !inputTouched,
    testId: ELEMENT.configurationFilePrettyPrintButton,
  },

  {
    name: inputTouched ? 'Save File' : 'No Changes',
    onClick: onSaveFile,
    variant: ButtonVariant.PRIMARY,
    disabled: !inputTouched,
    testId: ELEMENT.configurationFileSaveButton,
  },
];

const additionalLeftButtons = (onDeleteFile: () => void): Array<ButtonConfig> => [{
  name: 'Delete',
  onClick: onDeleteFile,
  variant: ButtonVariant.SECONDARY,
}];

const PageHeader: FC<PageHeaderProps> = ({
  name,
  inputTouched,
  isWriteDisabled,
  onSaveFile,
  onDeleteFile,
  onPrettyPrint,
}: PageHeaderProps) => {
  const headerProps: ConfigFileHeaderProps = { name };
  const nameAction = isWriteDisabled ? 'View' : 'Edit';
  switch (true) {
    case name?.startsWith('/dashboards/'): {
      const dashboardName = name.replace('/dashboards/', '');
      headerProps.viewLink = `/dash?page=${dashboardName}`;
      headerProps.type = 'Dashboard';
      headerProps.name = dashboardName;
      headerProps.helpLink = HELP_ROUTE.HELP_DASHBOARDS;
      break;
    }
    case name?.startsWith('/logParsers/'): {
      const parserName = name.replace('/logParsers/', '');
      headerProps.viewLink = `/parser?parser=${parserName}`;
      headerProps.type = 'Parser';
      headerProps.name = parserName;
      headerProps.helpLink = HELP_ROUTE.HELP_PARSING_LOGS;
      break;
    }
    case name?.startsWith('/datatables/'):
      headerProps.name = name;
      break;
    case name === '/scalyr/searches': {
      const savedSharedSearchesHelp = (
        <div>
          This file lists shortcuts to searches (or other Scalyr pages) you access frequently. Use the Record Search
          button in the search form to add new searches. Shortcuts in this file are shared by all Scalyr users in your
          account. Click
          <Link className="link" href="help/saved-searches">
            {' here '}
          </Link>
          for details.
        </div>
      );
      headerProps.customName = nameAction + ' Shared Searches';
      headerProps.extra = savedSharedSearchesHelp;
      break;
    }
    case name?.startsWith('/scalyr/alerts'): {
      headerProps.viewLink = '/alerts';
      headerProps.type = 'Alerts';
      headerProps.customName = nameAction + ' Alerts';
      headerProps.helpLink = HELP_ROUTE.HELP_ALERTS;
      break;
    }
    case name?.endsWith('/searches'): {
      const savedSearchesHelp = (
        <div>
          This file lists shortcuts to searches (or other Scalyr pages) you access frequently. Use the Save Search
          button in the search form to add new searches. Click
          <Link className="link" href={HELP_ROUTE.HELP_SAVED_SEARCHES}>
            {' here '}
          </Link>
          for details.
        </div>
      );
      headerProps.customName = nameAction + ' Saved Searches';
      headerProps.extra = savedSearchesHelp;
      break;
    }
    case name === '/scalyr/monitors': {
      headerProps.viewLink = '/monitors';
      headerProps.type = 'Monitors';
      headerProps.customName = nameAction + ' Monitors';
      headerProps.helpLink = HELP_ROUTE.HELP_MONITORS;
      break;
    }
    default:
      break;
  }

  const additionalLeftButtonGroup = (name === '/scalyr/logs') || isWriteDisabled ? [] : additionalLeftButtons(onDeleteFile);

  return (
    <div className="FilePage__PageHeaderDefault">
      <ScalyrPageHeader
        additionalLeftButtons={
          headerProps.type && headerProps.viewLink
            ? [
              { name: `View ${headerProps.type}`, link: headerProps.viewLink },
              ...additionalLeftButtonGroup,
            ]
            : additionalLeftButtonGroup
        }
        additionalRightButtons={additionalRightButtons(inputTouched, onSaveFile,  onPrettyPrint)}
        title={headerProps.customName || `${nameAction} ${headerProps.type || ' Configuration File'}: ${headerProps.name}`}
        hasBackButton
        helpLink={headerProps.helpLink}
      />
      {headerProps.extra || null}
    </div>
  );
};

export default PageHeader;
